import React, { useEffect } from 'react';
import { PartyPopper, Sparkles, Share2, X, BookOpen, ScrollText } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Plan completion celebration.
 *
 * Fires when a user marks the FINAL day of any plan complete. It's
 * intentionally warm and not gamified — no confetti-blast, no "level
 * up", just a well-done nod, a one-line reflection prompt and two
 * clear paths forward: share the plan or browse what's next.
 *
 * Props:
 *   open, onClose          — modal control
 *   planTitle              — displayed in the headline
 *   lengthDays             — displayed as "N-day journey"
 *   cover                  — accent color from the plan
 *   canShare               — true when this is a custom plan the
 *                            user owns; shows the Share CTA
 *   onShare, onBrowseMore  — click handlers for the two CTAs
 */
export default function PlanCompletionModal({
  open,
  onClose,
  planTitle,
  lengthDays,
  cover = 'var(--theme-accent)',
  canShare = false,
  onShare,
  onBrowseMore,
  onViewRecap,
}) {
  // Auto-dismiss on Escape so users on a laptop can bail without
  // reaching for the mouse.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.65)' }}
      onClick={onClose}
      data-testid="plan-completion-modal"
    >
      <div
        className="w-full max-w-md rounded-3xl p-6 relative"
        style={{
          background: 'var(--theme-background)',
          border: `1px solid ${cover}55`,
          boxShadow: `0 24px 48px -12px ${cover}44`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-2 rounded-full transition-all hover:bg-[var(--theme-hover)]"
          aria-label="Close"
          data-testid="plan-completion-close"
        >
          <X className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
        </button>

        <div
          className="h-14 w-14 rounded-2xl flex items-center justify-center mb-4"
          style={{ background: `${cover}22`, border: `1px solid ${cover}55` }}
        >
          <PartyPopper className="h-7 w-7" style={{ color: cover }} />
        </div>

        <p className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: cover }}>
          Well done
        </p>
        <h2 className="text-2xl font-bold leading-tight mb-2" style={{ color: 'var(--theme-text)' }}>
          You finished{planTitle ? ` ${planTitle}` : ' your plan'}!
        </h2>
        <p className="text-sm mb-5 leading-relaxed" style={{ color: 'var(--theme-textSecondary)' }}>
          {lengthDays
            ? `That's a ${lengthDays}-day journey through Scripture — take a breath and let today's truth settle.`
            : "Take a breath and let today's truth settle."}
        </p>

        <div
          className="rounded-xl p-3 mb-5 text-[13px] leading-relaxed"
          style={{ background: `${cover}0d`, border: `1px dashed ${cover}44`, color: 'var(--theme-text)', opacity: 0.9 }}
        >
          <span className="font-semibold" style={{ color: cover }}>One question to sit with: </span>
          What one thing from this plan do you want to keep with you into the coming week?
        </div>

        {onViewRecap && (
          <Button
            onClick={onViewRecap}
            className="w-full rounded-2xl py-5 gap-2 mb-2 text-sm font-semibold"
            style={{ background: cover, color: '#fff' }}
            data-testid="plan-completion-recap-btn"
          >
            <ScrollText className="h-4 w-4" />
            View your recap &amp; key takeaways
          </Button>
        )}
        {canShare && (
          <Button
            onClick={onShare}
            variant={onViewRecap ? 'outline' : 'default'}
            className="w-full rounded-2xl py-5 gap-2 mb-2 text-sm font-semibold"
            style={onViewRecap ? { borderColor: 'var(--glass-border)', color: 'var(--theme-text)' } : { background: cover, color: '#fff' }}
            data-testid="plan-completion-share-btn"
          >
            <Share2 className="h-4 w-4" />
            Share this plan with a friend
          </Button>
        )}
        <Button
          onClick={onBrowseMore}
          variant="outline"
          className="w-full rounded-2xl py-5 gap-2 text-sm font-semibold"
          style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-text)' }}
          data-testid="plan-completion-browse-btn"
        >
          {canShare ? <BookOpen className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
          Browse more study plans
        </Button>
      </div>
    </div>
  );
}
